import React, { useEffect, useCallback, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { refreshToken } from '../helpers/backend_helper'
import { shouldRefreshToken, isSessionExpired, storeTokenIssuedAt, clearSessionData } from '../helpers/session_helper'
import apiClientInstance, { TOKEN_REFRESH_LOCK_MS } from '../helpers/api_client'
import FullPageLoader from '../components/Common/FullPageLoader'

const useTokenRefresh = (checkIntervalMs = 60000) => { // Default check every minute
    const intervalRef = useRef(null)
    const refreshInProgressRef = useRef(false)
    const loaderRootRef = useRef(null)

    const showLoader = useCallback(() => {
        if (document.getElementById('token-refresh-loader')) return

        const container = document.createElement('div')
        container.id = 'token-refresh-loader'
        document.body.appendChild(container)

        loaderRootRef.current = createRoot(container)
        loaderRootRef.current.render(<FullPageLoader show={true} />)
    }, [])

    const hideLoader = useCallback(() => {
        const container = document.getElementById('token-refresh-loader')
        if (container) {
            if (loaderRootRef.current) {
                loaderRootRef.current.unmount()
                loaderRootRef.current = null
            }
            document.body.removeChild(container)
        }
    }, [])

    const performTokenRefresh = useCallback(async () => {
        // Prevent multiple simultaneous refresh attempts in this tab
        if (refreshInProgressRef.current) {
            console.log('[TOKEN REFRESH] ⏸️ Refresh already in progress in this tab, skipping...')
            return
        }

        // Check if another tab is refreshing (using localStorage as a lock)
        const refreshLock = localStorage.getItem('tokenRefreshLock')
        const now = Date.now()

        if (refreshLock) {
            const lockTime = parseInt(refreshLock)
            // If lock is less than TOKEN_REFRESH_LOCK_MS old, another tab is refreshing
            if (now - lockTime < TOKEN_REFRESH_LOCK_MS) {
                console.log('[TOKEN REFRESH] 🔒 Another tab is refreshing, skipping...')
                return
            } else {
                console.log('[TOKEN REFRESH] 🔓 Stale lock detected, proceeding with refresh')
            }
        }

        console.log('[TOKEN REFRESH] 🔄 Starting token refresh...')
        const refreshStartTime = new Date().toLocaleTimeString()
        refreshInProgressRef.current = true

        // Set lock to prevent other tabs from refreshing
        localStorage.setItem('tokenRefreshLock', now.toString())

        // Show loader
        showLoader()

        // Wait 0.5 seconds before API call to let loader appear
        await new Promise(resolve => setTimeout(resolve, 500))

        const isNetworkError = (err) =>
            err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || err.message === 'Network Error'

        let attempt = 0
        let succeeded = false

        try {
            while (!succeeded && !isSessionExpired()) {
                try {

                    const response = await refreshToken()

                    if (response && response.userCredentials) {
                        // Update session token in localStorage and apiClient
                        localStorage.setItem('_atk', response.userCredentials)
                        apiClientInstance.setAuth(response.userCredentials)

                        // Update token issued time
                        const newIssuedTime = storeTokenIssuedAt()

                        console.log('[TOKEN REFRESH] ✅ Token refreshed successfully at', refreshStartTime)
                        console.log('[TOKEN REFRESH] 📝 New token issued at:', new Date(newIssuedTime).toLocaleTimeString())
                        succeeded = true
                    } else {
                        throw new Error('Invalid refresh response')
                    }
                } catch (err) {
                    if (isNetworkError(err) && !isSessionExpired()) {
                        console.warn(`[TOKEN REFRESH] ⚠️ Network/timeout error (attempt ${attempt + 1}), retrying in 200ms:`, err.message)
                        hideLoader()
                        await new Promise(resolve => setTimeout(resolve, 200))
                        attempt++
                    } else {
                        throw err
                    }
                }
            }
        } catch (error) {
            console.error('[TOKEN REFRESH] ❌ Token refresh failed at', refreshStartTime, ':', error)

        } finally {
            console.log('[TOKEN REFRESH] Finally reached — succeeded:', succeeded)
            refreshInProgressRef.current = false
            // Release lock
            localStorage.removeItem('tokenRefreshLock')
            // Hide loader
            hideLoader()
        }
    }, [showLoader, hideLoader])

    const checkTokenRefresh = useCallback(async () => {
        const checkTime = new Date().toLocaleTimeString()
        console.log('[TOKEN REFRESH] 🔍 Running refresh check at', checkTime)

        // Skip if no session config available yet
        const sessionConfig = localStorage.getItem('sessionConfig')
        if (!sessionConfig) {
            console.log('[TOKEN REFRESH] ⚠️ No session config available yet, skipping check')
            return
        }

        // Skip if session is completely expired
        if (isSessionExpired()) {
            console.log('[TOKEN REFRESH] ⏰ Session expired! Logging out...')
            clearSessionData()
            window.location.href = '/login'
            return
        }

        // Check if token should be refreshed
        const shouldRefresh = shouldRefreshToken()
        console.log('[TOKEN REFRESH] 🎯 Should refresh?', shouldRefresh)

        if (shouldRefresh) {
            await performTokenRefresh()
        } else {
            console.log('[TOKEN REFRESH] ✋ No refresh needed at this time')
        }
    }, [performTokenRefresh])

    // Start periodic token refresh checks
    const startTokenRefreshChecks = useCallback(() => {
        if (intervalRef.current) {
            console.log('[TOKEN REFRESH] ⚠️ Token refresh checks already running')
            return // Already running
        }

        console.log('[TOKEN REFRESH] 🚀 Starting token refresh checks (interval:', checkIntervalMs / 1000, 'seconds)')

        // Initial check
        checkTokenRefresh()

        // Set up interval for periodic checks
        intervalRef.current = setInterval(() => {
            checkTokenRefresh()
        }, checkIntervalMs)

        console.log('[TOKEN REFRESH] ✅ Token refresh interval established')
    }, [checkTokenRefresh, checkIntervalMs])

    // Stop periodic token refresh checks
    const stopTokenRefreshChecks = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    // Auto-start checks on mount
    useEffect(() => {
        startTokenRefreshChecks()

        // Listen for storage events to sync across tabs
        const handleStorageChange = (event) => {
            if (event.key === 'tokenIssuedAt' && event.newValue) {
                console.log('[TOKEN REFRESH] 📡 Token updated in another tab at:', new Date(parseInt(event.newValue)).toLocaleTimeString())
                console.log('[TOKEN REFRESH] 🔄 Syncing token state across tabs...')

                // Update the API client with the new token from localStorage
                const newToken = localStorage.getItem('_atk')
                if (newToken) {
                    apiClientInstance.setAuth(newToken)
                    console.log('[TOKEN REFRESH] ✅ Token synced from another tab')
                }
            }

            // If session token is cleared in another tab, logout this tab too
            if (event.key === '_atk' && !event.newValue) {
                console.log('[TOKEN REFRESH] 🚪 Session cleared in another tab, logging out...')
                window.location.href = '/login'
            }
        }

        window.addEventListener('storage', handleStorageChange)

        return () => {
            stopTokenRefreshChecks()
            window.removeEventListener('storage', handleStorageChange)
        }
    }, [startTokenRefreshChecks, stopTokenRefreshChecks])

    return {
        performTokenRefresh, // Manual refresh function
        startTokenRefreshChecks,
        stopTokenRefreshChecks,
        isRefreshing: refreshInProgressRef.current
    }
}

export default useTokenRefresh